<script>
    import {Dropdown, Tooltip} from 'flowbite';

    import classnames from 'classnames';

    import {RideTypeToRouteType, RouteComputationTypes} from '@js/components/modules/constants';
    import * as Utils from '@js/modules/utils';
    import I18n from '@js/modules/translations';
    import StorageService from '@js/modules/storage';
    import AnalyticsService from '@js/modules/analyticsService';

    import {mapStore} from '@js/stores/mapStore';

    import {showProductModal} from '@js/actions/basketActions';

    import {clickOutside} from '@js/components/themes/actions/clickOutside';

    import Icon from '@js/components/themes/Icon.svelte';
    import {ROUTE_TYPE_ICON} from '@js/components/themes/icons';

    const {
        defaultRouteType = window.currentUserRideType ? RideTypeToRouteType[window.currentUserRideType] : 'hike',
        isSnapToRoadChecked = true,
        useRouteType = true,
        maxRouteTypeChange,
        // isPoiModeChecked = false,
        onSnapToRoadChange,
        onRouteTypeChange,
        onBackToHomeClick,
        onUndoClick,
        onClearClick,
        onExportGPXClick,
        hasElevationData = false,
        isElevationEnabled = false,
        onElevationToggle,
        isSlopePercentageEnabled = false,
        onSlopePercentageChange,
        isSurfaceTypeEnabled = false,
        onSurfaceTypeChange,
        alwaysVisible = false
    } = $props();

    let routeTypeDropdown;
    let _previousRideType = defaultRouteType;

    let isSnapToRoad = $state(isSnapToRoadChecked);
    let routeType = $state(defaultRouteType);
    let isMapOptionsOpen = $state(false);
    let isRouteTypeAvailable = $state(true);

    const handleMapOptionsOutsideClick = () => {
        if (!isMapOptionsOpen) {
            return;
        }

        isMapOptionsOpen = false;
    };

    const _onSnapToRoadChange = (event) => {
        isSnapToRoad = event.target.checked;

        onSnapToRoadChange(isSnapToRoad);
    };

    const _onSlopePercentageChange = (event) => {
        if (onSlopePercentageChange) {
            AnalyticsService.trackSlopePercentageClick();

            onSlopePercentageChange(event.target.checked);
        }
    };

    const _onSurfaceTypeChange = (event) => {
        if (onSurfaceTypeChange) {
            AnalyticsService.trackSurfaceTypeClick();

            onSurfaceTypeChange(event.target.checked);
        }
    };

    const _onElevationToggle = (event) => {
        if (onElevationToggle) {
            onElevationToggle(event.target.checked);
        }
    };

    const _onRouteTypeChange = (newRouteType) => {
        routeType = newRouteType;
        routeTypeDropdown.hide();

        onRouteTypeChange(routeType);
    };

    const _handleBackHomeClick = () => {
        isMapOptionsOpen = false;

        onBackToHomeClick();
    };

    const _handleClearClick = () => {
        isMapOptionsOpen = false;

        onClearClick();
    };

    const _handleRouteTypeClick = (event) => {
        event.preventDefault();

        if (maxRouteTypeChange) {
            const mapMaxRouteTypeCount = StorageService.getItem('map_max_route_type_count') || 0;

            if (mapMaxRouteTypeCount >= maxRouteTypeChange) {
                isRouteTypeAvailable = false;
                showProductModal('map_max_route_type');
            }

            StorageService.setItem('map_max_route_type_count', mapMaxRouteTypeCount + 1);
        }
    };

    const _handleOpenMapOptions = () => {
        isMapOptionsOpen = !isMapOptionsOpen;
    };

    const routeTypeDropdownAction = (rideDropdownElement) => {
        $effect(() => {
            const rideDropdownTrigger = document.getElementById('ride-route-types');

            routeTypeDropdown = new Dropdown(rideDropdownElement, rideDropdownTrigger);

            if (!isRouteTypeAvailable) {
                const rideTooltipElement = document.getElementById('tooltip-route-type');
                const rideTooltipTrigger = document.getElementById('ride-route-types-tooltip');
                new Tooltip(rideTooltipElement, rideTooltipTrigger, {
                    placement: 'bottom',
                    triggerType: 'hover'
                });
            }
        });
    };

    const _handleExportGPXClick = () => {
        onExportGPXClick();
    };

    $effect(() => {
        if ($mapStore.currentRideType && _previousRideType !== $mapStore.currentRideType) {
            _previousRideType = $mapStore.currentRideType;

            routeType = RideTypeToRouteType[$mapStore.currentRideType];
        }
    });
</script>

{#snippet snapToRoadRender()}
    <div class="map-navigation-snap-to-road">
        <label class="flex flex-col items-center justify-center py-1 px-1 text-center text-primary-default cursor-pointer">
            <input type="checkbox"
                   class="sr-only peer snap-to-roads"
                   checked={isSnapToRoad}
                   onchange={_onSnapToRoadChange}
                   value="">
            <span class="relative w-8 h-3 mt-1 bg-gray-200 peer-focus:outline-hidden peer-focus:ring-2 peer-focus:ring-primary-500 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:h-4 peer-checked:after:w-4 peer-checked:after:start-[4px] peer-checked:after:top-[-2px] after:content-[''] after:absolute after:top-[0px] after:start-[0px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></span>

            <span class="button-label">
                {I18n.t('js.maps.toolbar.snap_to_road.title')}
            </span>
        </label>
    </div>
{/snippet}

{#snippet elevationRender()}
    <div class="map-navigation-elevation"
         class:opacity-50={!hasElevationData}>
        <label class="flex flex-col items-center justify-center py-1 px-1 text-center text-primary-default"
               class:cursor-pointer={hasElevationData}
               class:pointer-events-none={!hasElevationData}>
            <input type="checkbox"
                   class="sr-only peer"
                   checked={isElevationEnabled}
                   disabled={!hasElevationData}
                   onchange={_onElevationToggle}
                   value="">
            <span class="relative w-8 h-3 mt-1 bg-gray-200 peer-focus:outline-hidden peer-focus:ring-2 peer-focus:ring-primary-500 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:h-4 peer-checked:after:w-4 peer-checked:after:start-[4px] peer-checked:after:top-[-2px] after:content-[''] after:absolute after:top-[0px] after:start-[0px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></span>

            <span class="button-label">
                    {I18n.t('js.maps.toolbar.elevation.title')}
                </span>
        </label>
    </div>
{/snippet}

{#snippet slopePercentageRender()}
    <div class="map-navigation-slope-color"
         class:opacity-50={!hasElevationData}>
        <label class="flex flex-col items-center justify-center py-1 px-1 text-center text-primary-default"
               class:cursor-pointer={hasElevationData}
               class:pointer-events-none={!hasElevationData}>
            <input type="checkbox"
                   class="sr-only peer"
                   checked={isSlopePercentageEnabled}
                   disabled={!hasElevationData}
                   onchange={_onSlopePercentageChange}
                   value="">
            <span class="relative w-8 h-3 mt-1 bg-gray-200 peer-focus:outline-hidden peer-focus:ring-2 peer-focus:ring-primary-500 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:h-4 peer-checked:after:w-4 peer-checked:after:start-[4px] peer-checked:after:top-[-2px] after:content-[''] after:absolute after:top-[0px] after:start-[0px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></span>

            <span class="button-label">
                    {I18n.t('js.maps.toolbar.slope_color.title')}
                </span>
        </label>
    </div>
{/snippet}

{#snippet surfaceTypeRender()}
    <div class="map-navigation-surface-color">
        <label class="flex flex-col items-center justify-center py-1 px-1 text-center text-primary-default cursor-pointer">
            <input type="checkbox"
                   class="sr-only peer"
                   checked={isSurfaceTypeEnabled}
                   onchange={_onSurfaceTypeChange}
                   value="">
            <span class="relative w-8 h-3 mt-1 bg-gray-200 peer-focus:outline-hidden peer-focus:ring-2 peer-focus:ring-primary-500 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:h-4 peer-checked:after:w-4 peer-checked:after:start-[4px] peer-checked:after:top-[-2px] after:content-[''] after:absolute after:top-[0px] after:start-[0px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></span>

            <span class="button-label">
                    {I18n.t('js.maps.toolbar.surface_color.title')}
                </span>
        </label>
    </div>
{/snippet}

{#snippet undoRender()}
    <div class="map-navigation-control">
        <button class="control-button undo-control"
                type="button"
                title={I18n.t('js.maps.toolbar.undo')}
                onclick={onUndoClick}>
            <svg class="control-button-icon inline-flex w-6 h-6 my-1"
                 xmlns="http://www.w3.org/2000/svg"
                 viewBox="0 0 24 24"
                 fill="currentColor">
                <path d="M12.5,8C9.85,8 7.45,9 5.6,10.6L2,7V16H11L7.38,12.38C8.77,11.22 10.54,10.5 12.5,10.5C16.04,10.5 19.05,12.81 20.1,16L22.47,15.22C21.08,11.03 17.15,8 12.5,8Z"/>
            </svg>
            <span class="button-label">
                {I18n.t('js.maps.toolbar.undo')}
            </span>
        </button>
    </div>
{/snippet}

{#snippet backHomeRender()}
    <div class="map-navigation-back-to-home">
        <button class="control-button back-to-home"
                type="button"
                title={I18n.t('js.maps.toolbar.back_to_home')}
                onclick={_handleBackHomeClick}>
            <svg class="control-button-icon inline-flex w-6 h-6 my-1"
                 xmlns="http://www.w3.org/2000/svg"
                 viewBox="0 0 24 24"
                 fill="currentColor">
                <path d="M12,5V1L7,6L12,11V7A6,6 0 0,1 18,13A6,6 0 0,1 12,19A6,6 0 0,1 6,13H4A8,8 0 0,0 12,21A8,8 0 0,0 20,13A8,8 0 0,0 12,5Z"/>
            </svg>
            <span class="button-label">
                {I18n.t('js.maps.toolbar.back_to_home')}
            </span>
        </button>
    </div>
{/snippet}

{#snippet clearRender()}
    <div class="map-navigation-control">
        <button class="control-button clear-control"
                type="button"
                title={I18n.t('js.maps.toolbar.clear')}
                onclick={_handleClearClick}>
            <svg class="control-button-icon inline-flex w-6 h-6 my-1"
                 xmlns="http://www.w3.org/2000/svg"
                 viewBox="0 0 24 24"
                 fill="currentColor">
                <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z"/>
            </svg>
            <span class="button-label">
                {I18n.t('js.maps.toolbar.clear')}
            </span>
        </button>
    </div>
{/snippet}

{#snippet exportGPXRender()}
    <div class="map-navigation-control">
        <button class="control-button clear-control"
                type="button"
                title={I18n.t('js.maps.toolbar.export_gpx')}
                onclick={_handleExportGPXClick}>
            <svg class="control-button-icon inline-flex w-4 h-4 my-1"
                 xmlns="http://www.w3.org/2000/svg"
                 viewBox="0 0 24 24"
                 fill="currentColor">
                <path d="M5,20H19V18H5M19,9H15V3H9V9H5L12,16L19,9Z"/>
            </svg>
            <span class="button-label">
                {I18n.t('js.maps.toolbar.export_gpx')}
            </span>
        </button>
    </div>
{/snippet}

<div class={classnames('map-navigation-container', {'map-navigation-container-always-visible': alwaysVisible})}>
    {#if onSnapToRoadChange}
        {@render snapToRoadRender()}
    {/if}

    {#if onElevationToggle}
        {@render elevationRender()}
    {/if}

    {#if onSlopePercentageChange}
        {@render slopePercentageRender()}
    {/if}

    {#if onSurfaceTypeChange}
        {@render surfaceTypeRender()}
    {/if}

    {#if useRouteType}
        <div id="route-type-element"
             class="map-navigation-route-type">
            <div id="ride-route-types-tooltip"
                 class="w-fit mx-auto"
                 data-tooltip-target={isRouteTypeAvailable ? undefined : 'tooltip-route-type'}
                 data-tooltip-placement="bottom">
                <div class="flex">
                    <button type="button"
                            id="ride-route-types"
                            class="button-label shrink-0 inline-flex items-center py-1.5 px-2 font-medium text-center text-primary-default bg-white border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-200 focus:ring-4 focus:outline-hidden focus:ring-gray-100 dark:bg-gray-700 dark:hover:bg-gray-600 dark:focus:ring-gray-700 dark:text-white dark:border-gray-600"
                            data-dropdown-toggle="route-type-dropdown"
                            onclick={_handleRouteTypeClick}>
                        <Icon class="h-4 w-4 me-2"
                              icon={ROUTE_TYPE_ICON[routeType]}/>
                        {I18n.t(`js.maps.toolbar.route_type.types.${routeType}`)}
                        <svg class="w-2.5 h-2.5 ms-2.5"
                             aria-hidden="true"
                             xmlns="http://www.w3.org/2000/svg"
                             fill="none"
                             viewBox="0 0 10 6">
                            <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                  d="m1 1 4 4 4-4"/>
                        </svg>
                    </button>

                    <div use:routeTypeDropdownAction
                         id="route-type-dropdown"
                         class="hidden w-36 bg-white divide-y divide-gray-100 rounded-lg shadow-sm z-dropdown dark:bg-gray-700">
                        <ul class="py-1 text-sm text-gray-700 dark:text-gray-200"
                            aria-labelledby="ride-route-types">
                            {#each Object.entries(Utils.sortObjectByKeys(I18n.t('js.maps.toolbar.route_type.types'), RouteComputationTypes)) as [type, routeName] (type)}
                                <li>
                                    <button type="button"
                                            class="inline-flex w-full px-2 py-1 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-600 dark:hover:text-white"
                                            class:bg-primary-200={type === routeType}
                                            onclick={() => _onRouteTypeChange(type)}>
                                        <span class="inline-flex items-center whitespace-nowrap">
                                            <Icon class="h-4 w-4 me-3"
                                                  icon={ROUTE_TYPE_ICON[type]}/>
                                            {routeName}
                                        </span>
                                    </button>
                                </li>
                            {/each}
                        </ul>
                    </div>
                </div>
            </div>

            {#if !isRouteTypeAvailable}
                <div id="tooltip-route-type"
                     role="tooltip"
                     class="absolute invisible inline-block px-3 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg shadow-xs opacity-0 tooltip z-tooltip dark:bg-gray-700">
                    {I18n.t('js.maps.toolbar.route_type.tooltip')}
                    <div class="tooltip-arrow"
                         data-popper-arrow></div>
                </div>
            {/if}
        </div>
    {/if}

    {#if onExportGPXClick}
        {@render exportGPXRender()}
    {/if}
</div>

{#if onUndoClick || onBackToHomeClick || onClearClick}
    <div class="map-navigation-control-container">
        {#if onUndoClick}
            {@render undoRender()}
        {/if}

        {#if onBackToHomeClick}
            {@render backHomeRender()}
        {/if}

        {#if onClearClick}
            {@render clearRender()}
        {/if}
    </div>
{/if}

{#if onUndoClick || onBackToHomeClick || onClearClick}
    <div class="map-navigation-control-container-mobile">
        <div class={classnames('controls-container', {'map-style-open': isMapOptionsOpen})}>
            {#if isMapOptionsOpen}
                <div {@attach clickOutside(handleMapOptionsOutsideClick, 'map-options-button')}
                     class="map-options-modal">
                    <div class="map-options-modal-content">
                        <h3 class="map-options-modal-title">
                            {I18n.t('js.maps.toolbar.options.title')}
                        </h3>
                    </div>

                    {#if onSnapToRoadChange}
                        {@render snapToRoadRender()}
                    {/if}

                    {#if onExportGPXClick}
                        {@render exportGPXRender()}
                    {/if}

                    {#if onUndoClick}
                        {@render undoRender()}
                    {/if}

                    {#if onBackToHomeClick}
                        {@render backHomeRender()}
                    {/if}

                    {#if onClearClick}
                        {@render clearRender()}
                    {/if}

                    <button class="map-options-close"
                            aria-label="Close"
                            onclick={_handleOpenMapOptions}>
                        <svg class="w-3 h-3"
                             aria-hidden="true"
                             xmlns="http://www.w3.org/2000/svg"
                             fill="none"
                             viewBox="0 0 14 14">
                            <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                  d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"/>
                        </svg>
                    </button>
                </div>
            {:else}
                {#if onUndoClick}
                    {@render undoRender()}
                {/if}

                <div class="map-navigation-control">
                    <button id="map-options-button"
                            class="control-button"
                            type="button"
                            title={I18n.t('js.maps.toolbar.options.more')}
                            onclick={_handleOpenMapOptions}>
                        <svg class="control-button-icon inline-flex w-6 h-6c my-1"
                             xmlns="http://www.w3.org/2000/svg"
                             viewBox="0 0 24 24"
                             fill="currentColor">
                            <path d="M16,12A2,2 0 0,1 18,10A2,2 0 0,1 20,12A2,2 0 0,1 18,14A2,2 0 0,1 16,12M10,12A2,2 0 0,1 12,10A2,2 0 0,1 14,12A2,2 0 0,1 12,14A2,2 0 0,1 10,12M4,12A2,2 0 0,1 6,10A2,2 0 0,1 8,12A2,2 0 0,1 6,14A2,2 0 0,1 4,12Z"/>
                        </svg>
                    </button>
                </div>
            {/if}
        </div>
    </div>
{/if}
